import { useEffect, useState, type ReactNode } from 'react'

import { fetchAppConfig } from '@/api/appConfig'
import { isBelowMinimum } from '@/lib/appVersion'
import { getNativeAppVersion, getPlatform, isNativePlatform } from '@/platform'

/**
 * Blocks the native app with an "Update required" screen when the installed
 * build is older than the store minimum. Fails open: no config or version
 * means no block. Renders children untouched on web.
 */
export function UpdateRequiredGate({ children }: { children: ReactNode }) {
  const [updateUrl, setUpdateUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!isNativePlatform()) return
    const platform = getPlatform() as 'ios' | 'android'
    let cancelled = false
    void (async () => {
      try {
        const [version, config] = await Promise.all([getNativeAppVersion(), fetchAppConfig()])
        if (cancelled || !version) return
        if (isBelowMinimum(version, config.min_app_version[platform] ?? '')) {
          setUpdateUrl(config.update_url[platform] ?? '')
        }
      } catch {
        // Fail open.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  if (updateUrl === null) return <>{children}</>

  return (
    <div
      role="alertdialog"
      aria-labelledby="update-required-title"
      className="bg-background fixed inset-0 z-[100] flex flex-col items-center justify-center gap-4 p-8 text-center"
    >
      <h1 id="update-required-title" className="text-xl font-semibold">
        Update required
      </h1>
      <p className="text-muted-foreground max-w-sm text-sm">
        This version of Maxflow is no longer supported. Update the app to continue.
      </p>
      {updateUrl && (
        <button
          type="button"
          onClick={() => window.open(updateUrl, '_blank')}
          className="bg-primary text-primary-foreground min-h-11 rounded-lg px-6 text-sm font-medium"
        >
          Update
        </button>
      )}
    </div>
  )
}
